import { CollapsibleFilters } from '../common/CollapsibleFilters';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Table,
  Typography,
  IconButton,
  Chip,
  Input,
  LinearProgress,
  Alert,
  Stack,
} from '@mui/joy';
import { Plus as AddIcon, PencilSimple as EditIcon, Trash as DeleteIcon, MagnifyingGlass as SearchIcon } from '@phosphor-icons/react';
import { distributorsApi, type Distributor } from '../../lib/distributorsApi';
import { useAuth } from '../../contexts/AuthContext';
import { extractApiError } from '../../utils/apiError';

interface Props {
  onEdit: (distributor: Distributor) => void;
  onAdd: () => void;
  refreshTrigger?: number;
}

export default function DistributorList({ onEdit, onAdd, refreshTrigger }: Props) {
  const { user } = useAuth();
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 20;

  const isAdmin = (user as any)?.role === 'admin' || (user as any)?.is_organization_admin;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    loadDistributors();
  }, [searchTerm, currentPage, refreshTrigger]);

  const loadDistributors = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await distributorsApi.list({
        search: searchTerm || undefined,
        page: currentPage,
      });
      setDistributors(response?.results || []);
      setTotalCount(response?.count || 0);
    } catch (err: any) {
      setError(extractApiError(err, 'Failed to load distributors'));
      setDistributors([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this distributor?')) {
      return;
    }

    try {
      await distributorsApi.delete(id);
      await loadDistributors();
    } catch (err: any) {
      setError(extractApiError(err, 'Failed to delete distributor'));
    }
  };

  const handleToggleActive = async (distributor: Distributor) => {
    try {
      if (distributor.is_active) {
        await distributorsApi.deactivate(distributor.id);
      } else {
        await distributorsApi.activate(distributor.id);
      }
      await loadDistributors();
    } catch (err: any) {
      setError(extractApiError(err, 'Failed to update distributor status'));
    }
  };

  return (
    <Card>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography level="h3">Distributors</Typography>
          {isAdmin && (
            <Button
              startDecorator={<AddIcon />}
              onClick={onAdd}
            >
              Add Distributor
            </Button>
          )}
        </Box>

        <CollapsibleFilters><Input
          placeholder="Search distributors..."
          startDecorator={<SearchIcon />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
        /></CollapsibleFilters>

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && <LinearProgress />}

        {!loading && distributors && distributors.length === 0 && (
          <Alert color="neutral">
            No distributors found. {isAdmin && 'Click "Add Distributor" to create one.'}
          </Alert>
        )}

        {!loading && distributors && distributors.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Sales Rep</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Status</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {distributors && distributors.map((distributor) => (
                  <tr key={distributor.id}>
                    <td>
                      <Typography level="body-md" fontWeight="lg">
                        {distributor.name}
                      </Typography>
                    </td>
                    <td>{distributor.sales_rep_name}</td>
                    <td>{distributor.phone_number}</td>
                    <td>{distributor.default_order_email}</td>
                    <td>
                      <Chip
                        color={distributor.is_active ? 'success' : 'neutral'}
                        size="sm"
                        onClick={isAdmin ? () => handleToggleActive(distributor) : undefined}
                        sx={{ cursor: isAdmin ? 'pointer' : 'default' }}
                      >
                        {distributor.is_active ? 'Active' : 'Inactive'}
                      </Chip>
                    </td>
                    {isAdmin && (
                      <td>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="sm"
                            variant="outlined"
                            onClick={() => onEdit(distributor)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="sm"
                            variant="outlined"
                            color="danger"
                            onClick={() => handleDelete(distributor.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        )}
      {/* Pagination */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography level="body-sm" color="neutral">
          {totalCount > 0
            ? `Showing ${Math.min((currentPage - 1) * PAGE_SIZE + 1, totalCount)}–${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount} distributors`
            : 'No distributors'}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            disabled={currentPage >= Math.ceil(totalCount / PAGE_SIZE)}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            Next
          </Button>
        </Stack>
      </Box>
      </Box>
    </Card>
  );
}
